import React, { createContext, useContext, useMemo } from "react";

import { useDashboardClusters } from "../../../hooks";
import { type DashboardClustersResponse } from "../../../types";
import { TimeRange } from "../../ClusterDetail/Components/TimeRangeSelector";

export type ClusterDashboardContextType = {
  dashboardClusters: DashboardClustersResponse;
  loading: boolean;
  timeRange?: TimeRange;
};

export const ClusterDashboardContext = createContext<ClusterDashboardContextType>({
  dashboardClusters: {
    clusters: [],
    summary: {
      cost: 0,
      previousCost: 0,
      waste: 0,
      previousWaste: 0,
    },
  },
  loading: true,
  timeRange: TimeRange.T_30D,
});

interface ClusterDashboardContextProviderProps {
  timeRange?: TimeRange;
  children: React.ReactNode;
}

export const ClusterDashboardContextProvider = ({ children, timeRange }: ClusterDashboardContextProviderProps) => {
  const { dashboardClusters, loading } = useDashboardClusters(timeRange);

  const value: ClusterDashboardContextType = useMemo(
    () => ({
      dashboardClusters,
      loading,
    }),
    [dashboardClusters, loading]
  );

  return <ClusterDashboardContext.Provider value={value}>{children}</ClusterDashboardContext.Provider>;
};

export const useClusterDashboardContext = () => {
  const context = useContext(ClusterDashboardContext);

  if (!context) {
    throw new Error("useClusterDashboardContext must be used within a ClusterDashboardContextProvider");
  }

  return context;
};
